import React, { useCallback, useEffect, useState, useRef } from 'react';
import { MicIcon, SquareIcon, XIcon, PlusIcon, CheckIcon, PencilIcon, SparklesIcon, LoaderIcon, AlertCircleIcon, RefreshCwIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps, WorkConditionCache } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { EditItemModal } from '../ui/EditItemModal';
import OpenAI from 'openai';
import { CONDITIONS_GENERATION_PROMPT, CONDITIONS_VOICE_EXTRACTION_PROMPT } from '../../config/aiPrompts';
import { useI18n } from '../../config/i18n';
// ── OpenAI helpers ───────────────────────────────────────────────────────────
const STORAGE_KEY = 'ptp_openai_api_key';
function getSavedApiKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}
function getOpenAIClient(): OpenAI {
  const apiKey = getSavedApiKey();
  return new OpenAI({
    apiKey: apiKey || 'missing-key',
    dangerouslyAllowBrowser: true
  });
}
// ── Constants & Types ────────────────────────────────────────────────────────
type ECMCategory = 'E' | 'C' | 'M';
const DEFAULT_OPTIONS: {
  text: string;
  category: ECMCategory;
}[] = [{
  text: 'Work area or site conditions affect the task',
  category: 'E'
}, {
  text: 'Weather or surrounding environment affects the task',
  category: 'E'
}, {
  text: 'Other crews, equipment, or nearby structures affect the task',
  category: 'E'
}, {
  text: 'Access, visibility, or space limits how the work is performed',
  category: 'E'
}, {
  text: 'Special skill, training, or authorization is required',
  category: 'C'
}, {
  text: 'The crew is unfamiliar with the task, tools, or work area',
  category: 'C'
}, {
  text: 'Time pressure, fatigue, or routine work may affect decisions',
  category: 'M'
}];
interface ConditionItem {
  id: string;
  text: string;
  relevant: boolean | null;
  category: ECMCategory | null;
}
interface ExtractedCondition {
  text: string;
  category: ECMCategory;
  selected: boolean;
}
let conditionIdCounter = 0;
function createCondition(text: string, relevant: boolean | null = null, category: ECMCategory | null = null, text_es?: string): ConditionItem {
  return {
    id: `cond-${++conditionIdCounter}`,
    text,
    text_es,
    relevant,
    category
  };
}
// Helper to get color classes for ECM badges
function getEcmColorClasses(category: ECMCategory | null) {
  switch (category) {
    case 'E':
      return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    case 'C':
      return 'bg-blue-50 text-blue-600 border-blue-200';
    case 'M':
      return 'bg-amber-50 text-amber-600 border-amber-200';
    default:
      return 'bg-gray-100 text-gray-500 border-gray-300';
  }
}
export function WorkEnvironmentScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const {
    lang,
    t
  } = useI18n();
  // ── State ──────────────────────────────────────────────────────────────────
  const [conditions, setConditions] = useState<ConditionItem[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(true); // Start true to check on mount
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const hasRunAI = useRef(false);
  // Manual Add State
  const [showAddInput, setShowAddInput] = useState(false);
  const [newConditionText, setNewConditionText] = useState('');
  // Edit State
  const [editingCondition, setEditingCondition] = useState<{
    id: string;
    text: string;
  } | null>(null);
  // Voice Modal State
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [extractedConditions, setExtractedConditions] = useState<ExtractedCondition[]>([]);
  // MediaRecorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // ── Sync to Parent ─────────────────────────────────────────────────────────
  const syncToParent = useCallback((items: ConditionItem[]) => {
    updateData({
      environmentConditions: items.filter(c => c.relevant === true).map(c => c.text),
      _workConditionsCache: items.map(c => ({
        id: c.id,
        text: c.text,
        relevant: c.relevant,
        category: c.category
      }))
    });
  }, [updateData]);
  // ── Initialization & AI Generation ─────────────────────────────────────────
  const loadFallbackConditions = useCallback(() => {
    const customSelected = data.environmentConditions.filter(c => !DEFAULT_OPTIONS.some(opt => opt.text === c));
    const customItems = customSelected.map(c => createCondition(c, true, null));
    const defaultItems = DEFAULT_OPTIONS.map(opt => createCondition(opt.text, data.environmentConditions.includes(opt.text) ? true : null, opt.category));
    const initialItems = [...customItems, ...defaultItems];
    setConditions(initialItems);
    // Cache the fallback state too so it persists across navigation
    updateData({
      _workConditionsCache: initialItems.map(c => ({
        id: c.id,
        text: c.text,
        relevant: c.relevant,
        category: c.category
      }))
    });
  }, [data.environmentConditions, updateData]);
  useEffect(() => {
    if (hasRunAI.current) return;
    hasRunAI.current = true;
    // If we have a cached conditions state (user navigated back), restore it exactly
    // But only if the cache has AI-generated conditions, not just the nearby task condition
    if (data._workConditionsCache && data._workConditionsCache.length > 0) {
      const hasAiConditions = data._workConditionsCache.some(c => !c.id.startsWith('cond-nearby'));
      if (hasAiConditions) {
        const restored = data._workConditionsCache.map(c => createCondition(c.text, c.relevant, c.category, c.text_es));
        setConditions(restored);
        setIsAnalyzing(false);
        return;
      }
      // Cache only has nearby condition — fall through to generate AI conditions
    }
    const apiKey = getSavedApiKey();
    const hasContext = data.taskDescription || data.jobSteps.length > 0;
    if (!apiKey || !hasContext || data.environmentConditions.length > 0) {
      loadFallbackConditions();
      setIsAnalyzing(false);
      return;
    }
    async function generateConditions() {
      setIsAnalyzing(true);
      setAnalyzeError(null);
      try {
        const client = getOpenAIClient();
        const jobStepsText = data.jobSteps.map((s, i) => `${i + 1}. ${s}`).join('\n');
        const completion = await client.chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: CONDITIONS_GENERATION_PROMPT
          }, {
            role: 'user',
            content: `Task: ${data.taskDescription}\n\nJob Steps:\n${jobStepsText}`
          }],
          temperature: 0.3,
          max_tokens: 500
        });
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) {
          jsonStr = fenceMatch[1].trim();
        }
        const parsed = JSON.parse(jsonStr) as {
          conditions: {
            text: string;
            text_es?: string;
            category: ECMCategory;
          }[];
        };
        if (!parsed.conditions || !Array.isArray(parsed.conditions)) {
          throw new Error('Invalid response structure');
        }
        // Initialize all as null (not reviewed yet)
        const aiItems = parsed.conditions.map(c => createCondition(c.text, null, c.category, c.text_es));
        // Preserve any existing nearby condition from the cache
        const nearbyItems = (data._workConditionsCache || []).filter(c => c.id.startsWith('cond-nearby')).map(c => createCondition(c.text, c.relevant, c.category, c.text_es));
        const allItems = [...nearbyItems, ...aiItems];
        setConditions(allItems);
        // Save cache so navigating back restores these exact conditions
        updateData({
          _workConditionsCache: allItems.map(c => ({
            id: c.id,
            text: c.text,
            text_es: c.text_es,
            relevant: c.relevant,
            category: c.category
          }))
        });
      } catch (err: any) {
        console.error('[AI Conditions] Failed:', err);
        setAnalyzeError('Failed to generate conditions. Using default list.');
        loadFallbackConditions();
      } finally {
        setIsAnalyzing(false);
      }
    }
    generateConditions();
  }, [data.taskDescription, data.jobSteps, data.environmentConditions.length, loadFallbackConditions, syncToParent]);
  // Cleanup media stream
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);
  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSetRelevant = (id: string, value: boolean) => {
    const updated = conditions.map(c => c.id === id ? {
      ...c,
      relevant: value
    } : c);
    setConditions(updated);
    syncToParent(updated);
  };
  const handleAddCondition = () => {
    const trimmed = newConditionText.trim();
    if (trimmed && !conditions.some(c => c.text === trimmed)) {
      // Manual additions don't have an AI category by default
      const newItem = createCondition(trimmed, true, null);
      const updated = [newItem, ...conditions];
      setConditions(updated);
      syncToParent(updated);
    }
    setNewConditionText('');
    setShowAddInput(false);
  };
  const handleRetryAnalysis = () => {
    hasRunAI.current = false;
    setAnalyzeError(null);
    setConditions([]);
    // Force re-render to trigger useEffect
  };
  // ── Edit Condition Handler ──────────────────────────────────────────────────
  const handleEditCondition = (id: string, newText: string) => {
    const updated = conditions.map(c => c.id === id ? {
      ...c,
      text: newText
    } : c);
    setConditions(updated);
    syncToParent(updated);
    setEditingCondition(null);
  };
  // ── Voice Recording & Extraction ───────────────────────────────────────────
  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      setVoiceTranscription('');
      setHasRecorded(false);
      setExtractedConditions([]);
    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceError('Microphone access denied. Please allow permission.');
    }
  }, []);
  const stopRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    setIsRecording(false);
    setIsTranscribing(true);
    setVoiceError(null);
    const audioBlob = await new Promise<Blob>(resolve => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunksRef.current, {
          type: 'audio/webm'
        }));
      };
      mediaRecorder.stop();
    });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioBlob.size < 1000) {
      setIsTranscribing(false);
      setVoiceError('Recording too short. Please speak longer.');
      return;
    }
    try {
      const file = new File([audioBlob], 'conditions.webm', {
        type: 'audio/webm'
      });
      const client = getOpenAIClient();
      // 1. Transcribe
      const transcriptResponse = await client.audio.transcriptions.create({
        model: 'whisper-1',
        file,
        language: lang === 'es' ? 'es' : 'en'
      });
      const rawText = transcriptResponse.text.trim();
      if (!rawText) {
        setIsTranscribing(false);
        setVoiceError('No speech detected. Please try again.');
        return;
      }
      setVoiceTranscription(rawText);
      setHasRecorded(true);
      setIsTranscribing(false);
      setIsExtracting(true);
      // 2. Extract Conditions
      const completion = await client.chat.completions.create({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: CONDITIONS_VOICE_EXTRACTION_PROMPT
        }, {
          role: 'user',
          content: `Transcription: "${rawText}"`
        }],
        temperature: 0.3,
        max_tokens: 300
      });
      const gptRaw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = gptRaw;
      const fenceMatch = gptRaw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr) as {
        conditions: {
          text: string;
          category: ECMCategory;
        }[];
      };
      if (parsed.conditions && Array.isArray(parsed.conditions)) {
        setExtractedConditions(parsed.conditions.map(c => ({
          text: c.text,
          category: c.category,
          selected: true // Auto-select extracted conditions
        })));
      } else {
        throw new Error('Invalid extraction format');
      }
    } catch (err: any) {
      console.error('[Voice Extraction] Failed:', err);
      setVoiceError(`Failed to process audio: ${err?.message || String(err)}`);
    } finally {
      setIsTranscribing(false);
      setIsExtracting(false);
    }
  }, []);
  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  const toggleExtracted = (index: number) => {
    setExtractedConditions(prev => prev.map((c, i) => i === index ? {
      ...c,
      selected: !c.selected
    } : c));
  };
  const handleApplyVoiceConditions = () => {
    const selected = extractedConditions.filter(c => c.selected);
    let updated = [...conditions];
    const toAdd: ConditionItem[] = [];
    for (const item of selected) {
      if (!updated.some(c => c.text === item.text)) {
        toAdd.push(createCondition(item.text, true, item.category));
      }
    }
    updated = [...toAdd, ...updated];
    setConditions(updated);
    syncToParent(updated);
    setShowVoiceModal(false);
    setTimeout(() => {
      setHasRecorded(false);
      setVoiceTranscription('');
      setExtractedConditions([]);
    }, 300);
  };
  const handleCloseVoiceModal = () => {
    if (isRecording || isTranscribing || isExtracting) return;
    setShowVoiceModal(false);
    setTimeout(() => {
      setHasRecorded(false);
      setVoiceTranscription('');
      setExtractedConditions([]);
      setVoiceError(null);
    }, 300);
  };
  // ── Render Helpers ─────────────────────────────────────────────────────────
  const reviewedCount = conditions.filter(c => c.relevant !== null).length;
  const totalCount = conditions.length;
  const selectedExtractedCount = extractedConditions.filter(c => c.selected).length;
  if (isAnalyzing) {
    return <div className="flex flex-col h-full items-center justify-center" data-id="element-1751">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'linear'
      }} className="mb-5" data-id="element-1752">
          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-1753" />
        </motion.div>
        <p className="text-gray-900 font-semibold text-base mb-1" data-id="element-1754">
          Analyzing task conditions…
        </p>
        <p className="text-gray-400 text-xs text-center max-w-[220px]" data-id="element-1755">
          AI is identifying relevant E/C/M conditions based on your task
          description.
        </p>
      </div>;
  }
  return <div className="flex flex-col h-full relative" data-id="element-1756">
      <div className="mb-2" data-id="element-1757">
        <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-1758">
          {t('workEnv.title')}
        </h2>
        <p className="text-gray-500 text-xs" data-id="element-1759">{t('workEnv.subtitle')}</p>
      </div>

      {/* Error banner */}
      <AnimatePresence data-id="element-1760">
        {analyzeError && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-3" data-id="element-1761">
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 flex items-start gap-2.5" data-id="element-1762">
              <AlertCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1763" />
              <div className="flex-1 min-w-0" data-id="element-1764">
                <p className="text-amber-700 text-xs leading-relaxed" data-id="element-1765">
                  {analyzeError}
                </p>
              </div>
              <button onClick={() => {
            hasRunAI.current = false;
            setConditions([]);
          }} className="flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-600 text-[10px] font-bold uppercase tracking-wider flex-shrink-0 hover:bg-amber-200 transition-colors" data-id="element-1766">
                <RefreshCwIcon className="w-3 h-3" data-id="element-1767" />
                Retry
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-3" data-id="element-1768">
        <div className="flex items-center gap-2" data-id="element-1769">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1770">
            {t('summary.conditions')}
          </p>
          <span className="text-xs text-gray-400 font-medium" data-id="element-1771">
            {reviewedCount}/{totalCount} {t('workEnv.reviewed')}
          </span>
          <button onClick={() => setShowAddInput(true)} className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-yellow-500 hover:border-gray-300 transition-colors" data-id="element-1772">
            <PlusIcon className="w-3.5 h-3.5" data-id="element-1773" />
          </button>
        </div>
        <button onClick={() => setShowVoiceModal(true)} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" data-id="element-1774">
          <MicIcon className="w-3.5 h-3.5 text-yellow-500" data-id="element-1775" />
          {t('workEnv.addCondition')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 space-y-2 -mx-2 px-2" data-id="element-1776">
        <AnimatePresence data-id="element-1777">
          {showAddInput && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="overflow-hidden" data-id="element-1778">
              <div className="bg-white border border-yellow-400 rounded-lg p-3 mb-2 flex items-center gap-2" data-id="element-1779">
                <input autoFocus value={newConditionText} onChange={e => setNewConditionText(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleAddCondition();
              if (e.key === 'Escape') {
                setShowAddInput(false);
                setNewConditionText('');
              }
            }} placeholder="Describe the condition..." className="flex-1 bg-transparent text-gray-900 text-sm outline-none placeholder-gray-400" data-id="element-1780" />
                <button onClick={handleAddCondition} disabled={!newConditionText.trim()} className="w-8 h-8 rounded-lg bg-yellow-400 flex items-center justify-center flex-shrink-0 disabled:opacity-30 disabled:bg-gray-200 transition-all" data-id="element-1781">
                  <CheckIcon className="w-4 h-4 text-gray-900" data-id="element-1782" />
                </button>
                <button onClick={() => {
              setShowAddInput(false);
              setNewConditionText('');
            }} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-400" data-id="element-1783">
                  <XIcon className="w-4 h-4" data-id="element-1784" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {conditions.map((condition, index) => <motion.div key={condition.id} initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.03
      }} className={`rounded-lg p-3 flex items-start gap-3 border transition-colors ${condition.relevant === true ? 'bg-emerald-50 border-emerald-200' : condition.relevant === false ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-white border-gray-200'}`} data-id="element-1785">
            {condition.category && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border flex-shrink-0 mt-0.5 ${getEcmColorClasses(condition.category)}`} data-id="element-1786">
                {condition.category}
              </span>}

            <button onClick={() => setEditingCondition({
          id: condition.id,
          text: condition.text
        })} className="flex-1 flex items-start gap-1.5 text-left group" data-id="element-1787">
              <p className={`text-sm leading-snug ${condition.relevant === true ? 'text-emerald-700' : condition.relevant === false ? 'text-gray-400 line-through' : 'text-gray-800'}`} data-id="element-1788">
                {lang === 'es' && condition.text_es ? condition.text_es : condition.text}
              </p>
              <PencilIcon className="w-3 h-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0 mt-0.5 transition-colors" data-id="element-1789" />
            </button>

            <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1790">
              <button onClick={() => handleSetRelevant(condition.id, true)} className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${condition.relevant === true ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30' : 'bg-gray-100 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600'}`} data-id="element-1791">
                <CheckIcon className="w-4 h-4" data-id="element-1792" />
              </button>
              <button onClick={() => handleSetRelevant(condition.id, false)} className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${condition.relevant === false ? 'bg-gray-400 text-white' : 'bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500'}`} data-id="element-1793">
                <XIcon className="w-4 h-4" data-id="element-1794" />
              </button>
            </div>
          </motion.div>)}
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-1795">
        <PrimaryButton onClick={onNext} disabled={reviewedCount < totalCount || totalCount === 0} data-id="element-1796">
          {t('continue')}
        </PrimaryButton>
      </div>

      {/* Edit Condition Modal */}
      <EditItemModal isOpen={!!editingCondition} initialText={editingCondition?.text || ''} title={t('edit')} onSave={newText => {
      if (editingCondition) handleEditCondition(editingCondition.id, newText);
    }} onCancel={() => setEditingCondition(null)} data-id="element-1797" />

      {/* ── Voice Input Modal ───────────────────────────────────────────────── */}
      <AnimatePresence data-id="element-1798">
        {showVoiceModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={handleCloseVoiceModal} className="absolute inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-1799" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 rounded-t-2xl z-50 px-5 pt-4 pb-6 max-h-[85%] flex flex-col overflow-hidden" data-id="element-1800">
              <div className="flex items-center justify-between mb-2" data-id="element-1801">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1802">
                  Add Conditions
                </h3>
                <button onClick={handleCloseVoiceModal} disabled={isRecording || isTranscribing || isExtracting} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100 disabled:opacity-50" data-id="element-1803">
                  <XIcon className="w-5 h-5" data-id="element-1804" />
                </button>
              </div>

              <p className="text-gray-500 text-sm mb-4" data-id="element-1805">
                Describe site conditions, crew experience, or time pressures. AI
                will extract specific items.
              </p>

              {/* Recording / Processing Section */}
              {extractedConditions.length === 0 && <div className="flex-1 flex flex-col items-center justify-center space-y-4 mb-4 min-h-0" data-id="element-1806">
                  {isTranscribing || isExtracting ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center space-y-4" data-id="element-1807">
                      <div className="w-16 h-16 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center" data-id="element-1808">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1.2,
                  ease: 'linear'
                }} data-id="element-1809">
                          <LoaderIcon className="w-7 h-7 text-yellow-500" data-id="element-1810" />
                        </motion.div>
                      </div>
                      <div className="text-center space-y-1" data-id="element-1811">
                        <p className="text-gray-900 font-medium text-sm" data-id="element-1812">
                          {isTranscribing ? 'Transcribing...' : 'Extracting conditions...'}
                        </p>
                        <p className="text-gray-400 text-xs" data-id="element-1813">
                          {isTranscribing ? 'Converting speech to text' : 'AI is identifying E/C/M conditions'}
                        </p>
                      </div>
                      {voiceTranscription && isExtracting && <div className="w-full p-3 rounded-xl bg-gray-50 border border-gray-200 mt-2 max-h-[100px] overflow-y-auto" data-id="element-1814">
                          <p className="text-gray-500 font-mono text-xs leading-snug" data-id="element-1815">
                            "{voiceTranscription}"
                          </p>
                        </div>}
                    </motion.div> : <>
                      <div className="relative flex items-center justify-center" data-id="element-1816">
                        {isRecording && <motion.div animate={{
                  scale: [1, 1.3, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.5
                }} className="absolute w-24 h-24 bg-yellow-100 rounded-full" data-id="element-1817" />}
                        {isRecording && <motion.div animate={{
                  scale: [1, 1.15, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  delay: 0.2
                }} className="absolute w-20 h-20 bg-yellow-200 rounded-full" data-id="element-1818" />}
                        <motion.button whileTap={{
                  scale: 0.9
                }} onClick={handleRecordToggle} className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-colors ${isRecording ? 'bg-red-500 shadow-red-400/30' : 'bg-yellow-400 shadow-yellow-400/30'}`} data-id="element-1819">
                          {isRecording ? <SquareIcon className="w-6 h-6 text-white fill-current" data-id="element-1820" /> : <MicIcon className="w-6 h-6 text-gray-900" data-id="element-1821" />}
                        </motion.button>
                      </div>

                      <div className="text-center h-6" data-id="element-1822">
                        <p className={`font-medium text-sm ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} data-id="element-1823">
                          {isRecording ? 'Recording...' : hasRecorded ? 'Tap to re-record' : 'Tap to start'}
                        </p>
                      </div>

                      {voiceError && <p className="text-red-500 text-xs text-center max-w-[250px] mt-2" data-id="element-1824">
                          {voiceError}
                        </p>}
                    </>}
                </div>}

              {/* Extracted Conditions List */}
              {extractedConditions.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex-1 flex flex-col min-h-0 mb-4" data-id="element-1825">
                  <div className="flex items-center gap-2 mb-3" data-id="element-1826">
                    <SparklesIcon className="w-4 h-4 text-yellow-500" data-id="element-1827" />
                    <p className="text-sm font-semibold text-gray-900" data-id="element-1828">
                      AI found {extractedConditions.length} conditions
                    </p>
                  </div>

                  <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-1" data-id="element-1829">
                    {extractedConditions.map((item, index) => <motion.button key={index} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} onClick={() => toggleExtracted(index)} className={`w-full rounded-lg p-3 flex items-start gap-3 border text-left transition-all ${item.selected ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-100 border-gray-200 opacity-60'}`} data-id="element-1830">
                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${item.selected ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'}`} data-id="element-1831">
                          {item.selected && <CheckIcon className="w-3.5 h-3.5" data-id="element-1832" />}
                        </div>

                        <div className="flex-1" data-id="element-1833">
                          <div className="flex items-center gap-2 mb-1" data-id="element-1834">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border flex-shrink-0 ${getEcmColorClasses(item.category)}`} data-id="element-1835">
                              {item.category}
                            </span>
                          </div>
                          <span className={`text-sm leading-snug ${item.selected ? 'text-emerald-700' : 'text-gray-500'}`} data-id="element-1836">
                            {item.text}
                          </span>
                        </div>
                      </motion.button>)}
                  </div>

                  <button onClick={() => {
              setExtractedConditions([]);
              setHasRecorded(false);
              setVoiceTranscription('');
            }} className="mt-3 text-xs text-gray-400 hover:text-gray-700 transition-colors text-center" data-id="element-1837">
                    Re-record instead
                  </button>
                </motion.div>}

              {/* Action Button */}
              {extractedConditions.length > 0 && <PrimaryButton onClick={handleApplyVoiceConditions} disabled={selectedExtractedCount === 0} data-id="element-1838">
                  Add {selectedExtractedCount} Condition
                  {selectedExtractedCount !== 1 ? 's' : ''}
                </PrimaryButton>}
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}